import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Helper para formatar valores em BRL
const formatBRL = (value) => {
  const num = typeof value === 'number' && isFinite(value) ? value : 0;
  // Formata com separador de milhares (.) e decimais (,)
  // Exemplo: 36174.00 -> 36.174,00
  return `R$ ${num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Helper para formatar data
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

// Helper para formatar CEP
const formatCEP = (cep) => {
  if (!cep) return '';
  const cleaned = cep.replace(/\D/g, '');
  if (cleaned.length === 8) {
    return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
  }
  return cep;
};

export const generateProposalPDF = async (quotation, filialData, clausulas, clientData = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  let yPosition = margin;

  const formData = quotation.data || {};
  const quotationId = quotation.quotation_id;
  const version = quotation.version || 1;

  // Cores personalizadas (diferentes do exemplo)
  const primaryColor = [34, 139, 34]; // Verde escuro
  const secondaryColor = [70, 130, 180]; // Azul aço
  const headerBg = [240, 248, 255]; // Azul claro

  // Preload images
  let logoImg = null;
  let watermarkImg = null;

  if (filialData && filialData.logo_url) {
    try {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = filialData.logo_url;
      await new Promise((resolve, reject) => {
        img.onload = () => {
          logoImg = img;
          resolve();
        };
        img.onerror = reject;
      });
    } catch (error) {
      console.error('Erro ao carregar logo:', error);
    }
  }

  // Função para adicionar marca d'água com logo
  const addWatermark = () => {
    if (logoImg) {
      const imgWidth = 100;
      const imgHeight = 100;
      const x = (pageWidth - imgWidth) / 2;
      const y = (pageHeight - imgHeight) / 2;

      // Reset graphics state to default
      doc.setGState(new doc.GState({ opacity: 1 }));

      // Set watermark opacity
      doc.setGState(new doc.GState({ opacity: 0.05 }));
      doc.addImage(logoImg, 'PNG', x, y, imgWidth, imgHeight);

      // Reset graphics state after watermark
      doc.setGState(new doc.GState({ opacity: 1 }));
    }
  };

  // Função para adicionar cabeçalho com logo
  const addHeader = () => {
    if (logoImg) {
      const imgWidth = 30;
      const imgHeight = 15;
      doc.addImage(logoImg, 'PNG', pageWidth - margin - imgWidth, 5, imgWidth, imgHeight);
    }

    // Título do documento
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text('ORÇAMENTO/PEDIDO DE VENDA', margin, yPosition);
    yPosition += 8;

    // Informações do orçamento
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(`${filialData?.nome || 'VENUS SOLUÇÕES INTEGRADAS LTDA'}`, margin, yPosition);
    yPosition += 5;
    doc.text(`CNPJ: ${filialData?.cnpj || ''}`, margin, yPosition);
    yPosition += 8;

    // Datas
    const today = new Date().toLocaleDateString('pt-BR');
    doc.setFontSize(9);
    doc.text(`Emissão: ${today}`, margin, yPosition);
    doc.text(`Impressão: ${today}`, margin + 60, yPosition);
    doc.text(`Previsão de Embarque: ${formatDate(formData.dataPrevistaEmbarque)}`, margin + 100, yPosition);
    yPosition += 5;
    doc.text(`Comprador: ${formData.comprador || ''}`, margin, yPosition);
    yPosition += 5;
    doc.text(`Vendedor: ${formData.vendedor || ''}`, margin, yPosition);
    doc.text(`Celular: ${formData.celularVendedor || ''}`, margin + 60, yPosition);
    yPosition += 5;
  };

  // Helper para truncar texto
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  };

  // Função para adicionar dados do cliente
  const addClientData = () => {
    // Fundo cinza para seção
    doc.setFillColor(...headerBg);
    doc.rect(margin, yPosition, pageWidth - 2 * margin, 8, 'F');
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('DADOS DO CLIENTE', margin + 2, yPosition + 5);
    yPosition += 12;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    const col1X = margin;
    const col2X = pageWidth / 2 + 5;
    const lineHeight = 5;

    // Coluna 1
    doc.text(`Cliente:`, col1X, yPosition);
    doc.text(truncateText(formData.cliente || '', 38), col1X + 25, yPosition);
    
    // Coluna 2 - Endereço de COBRANÇA
    doc.text(`Endereço:`, col2X, yPosition);
    const endereco = `${formData.logradouro || ''}, ${formData.numero || ''}`;
    doc.text(truncateText(endereco, 37), col2X + 25, yPosition);
    yPosition += lineHeight;

    doc.text(`E-mail:`, col1X, yPosition);
    const clientEmail = clientData?.email || '';
    doc.text(truncateText(clientEmail, 38), col1X + 25, yPosition);
    
    doc.text(`Cidade:`, col2X, yPosition);
    doc.text(truncateText(formData.cidade || '', 37), col2X + 25, yPosition);
    yPosition += lineHeight;

    doc.text(`Telefone(s):`, col1X, yPosition);
    // Combinar telefone e celular se ambos existirem
    const telefones = [];
    if (clientData?.telefone) telefones.push(clientData.telefone);
    if (clientData?.celular) telefones.push(clientData.celular);
    const telefonesText = telefones.join(' / ');
    doc.text(truncateText(telefonesText, 38), col1X + 25, yPosition);
    
    doc.text(`Bairro:`, col2X, yPosition);
    doc.text(truncateText(formData.bairro || '', 37), col2X + 25, yPosition);
    yPosition += lineHeight;

    doc.text(`Contato:`, col1X, yPosition);
    doc.text(truncateText(formData.comprador || '', 38), col1X + 25, yPosition);
    
    doc.text(`CEP:`, col2X, yPosition);
    doc.text(formatCEP(formData.cep), col2X + 25, yPosition);
    yPosition += lineHeight;

    doc.text(`Inscr. Estadual:`, col1X, yPosition);
    doc.text(truncateText(formData.inscricaoEstadual || '', 38), col1X + 25, yPosition);
    
    doc.text(`Estado:`, col2X, yPosition);
    doc.text(truncateText(formData.estado || '', 37), col2X + 25, yPosition);
    yPosition += lineHeight;

    doc.text(`CPF/CNPJ:`, col1X, yPosition);
    doc.text(truncateText(formData.cpf || formData.cnpj || '', 38), col1X + 25, yPosition);
    
    doc.text(`Contrib. ICMS:`, col2X, yPosition);
    doc.text(truncateText(formData.contribuinteICMS || '', 37), col2X + 25, yPosition);
    yPosition += 10;
  };

  // Função para adicionar tabela de materiais
  const addMaterialsTable = () => {
    const tableData = (formData.items || []).map((item, index) => {
      const valorTabela = item.valorTabela || 0;
      const over = item.over1Type === '%' ? valorTabela * (item.over1 || 0) / 100 : (item.over1 || 0);
      const valorLiquido = valorTabela + over;
      const freteUnitario = item.freteEmbutidoItem ? item.freteEmbutidoItem / (item.quantidade || 1) : 0;
      const valorVenda = valorLiquido + freteUnitario;
      const icms = item.icms || 0;
      const ipi = item.ipi || (item.product && item.product.ipi !== undefined ? item.product.ipi : 0);
      const quantidade = item.quantidade || 0;

      // Montar descrição do produto
      let descricao = item.product && item.product.code ? `${item.product.code} - ${item.produto || ''}` : (item.code ? `${item.code} - ${item.produto || ''}` : item.produto || '');
      
      // Adicionar NCM se existir
      if (item.ncm) {
        descricao += `\nNCM ${item.ncm}`;
      }
      
      // Adicionar relação de peças se existir
      if (item.pieceRelation && item.dimensions && item.dimensions.length > 0) {
        descricao += '\n';
        item.dimensions.forEach(dim => {
          const pcs = dim.pieces || 0;
          const length = dim.length || 0;
          const obs = dim.observacao ? ` ${dim.observacao}` : '';
          descricao += `\n${pcs} PEÇAS DE ${length} MM${obs}`;
        });
      }

      return [
        (index + 1).toString(),
        quantidade.toFixed(3).replace('.', ','),
        item.unidade || '',
        descricao,
        formatBRL(valorVenda),
        formatBRL(item.valorTotal || 0),
        `${icms}%`,
        `${ipi}%`
      ];
    });

    doc.autoTable({
      startY: yPosition,
      head: [['ITEM', 'QTD.', 'UNID', 'DESCRIÇÃO DOS MATERIAIS', 'VLR UNIT.', 'VLR TOTAL', 'ICMS', 'IPI']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: secondaryColor,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 8,
        cellPadding: 3
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { halign: 'center', cellWidth: 16 },
        2: { halign: 'center', cellWidth: 12 },
        3: { halign: 'left', cellWidth: 'auto' },
        4: { halign: 'right', cellWidth: 23 },
        5: { halign: 'right', cellWidth: 23 },
        6: { halign: 'center', cellWidth: 13 },
        7: { halign: 'center', cellWidth: 13 }
      },
      margin: { top: 80, left: margin, right: margin, bottom: 80 },
      pageBreak: 'auto',
      didDrawPage: (data) => {
        // Add watermark and header synchronously for table pages (except first page)
        addWatermark();
        if (data.pageNumber > 1) {
          let headerY = margin;
          if (logoImg) {
            doc.addImage(logoImg, 'PNG', pageWidth - margin - 30, 5, 30, 15);
          }
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...primaryColor);
          doc.text('ORÇAMENTO/PEDIDO DE VENDA', margin, headerY);
          headerY += 8;
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 0, 0);
          doc.text(`${filialData?.nome || 'VENUS SOLUÇÕES INTEGRADAS LTDA'}`, margin, headerY);
          headerY += 5;
          doc.text(`CNPJ: ${filialData?.cnpj || ''}`, margin, headerY);
          headerY += 8;
          const today = new Date().toLocaleDateString('pt-BR');
          doc.setFontSize(9);
          doc.text(`Emissão: ${today}`, margin, headerY);
          doc.text(`Impressão: ${today}`, margin + 60, headerY);
          doc.text(`Previsão de Embarque: ${formatDate(formData.dataPrevistaEmbarque)}`, margin + 120, headerY);
          headerY += 5;
          doc.text(`Comprador: ${formData.comprador || ''}`, margin, headerY);
          headerY += 5;
          doc.text(`Vendedor: ${formData.vendedor || ''}`, margin, headerY);
          doc.text(`Celular: ${formData.celularVendedor || ''}`, margin + 60, headerY);
        }

        addFooter(doc.internal.getNumberOfPages());
      }
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  };

  // Função para adicionar valores do pedido
  const addOrderValues = () => {
    const itemsTotal = (formData.items || []).reduce((sum, item) => sum + (item.valorTotal || 0), 0);
    const freteTotal = Number(formData.freteDestacadoCalculado || 0);

    // Calculate total over for discount calculation
    const totalOver = (formData.items || []).reduce((sum, item) => {
      const valorTabela = item.valorTabela || 0;
      const over = item.over1Type === '%' ? valorTabela * (item.over1 || 0) / 100 : (item.over1 || 0);
      return sum + over * (item.quantidade || 0);
    }, 0);

    // Calculate discount
    let descontoAmount = 0;
    if (formData.desconto && formData.desconto > 0) {
      if (formData.descontoType === '%') {
        descontoAmount = totalOver * (parseFloat(formData.desconto) / 100);
      } else {
        descontoAmount = parseFloat(formData.desconto) || 0;
      }
    }

    const totalPedido = itemsTotal + freteTotal - descontoAmount;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Valores do pedido', margin, yPosition);
    yPosition += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);

    const valuesData = [
      ['Total Itens', formatBRL(itemsTotal)],
      ['Total Frete', formatBRL(freteTotal)],
    ];

    valuesData.forEach(([label, value]) => {
      doc.text(label, margin, yPosition);
      doc.text(value, pageWidth - margin - 30, yPosition, { align: 'right' });
      yPosition += 5;
    });

    // Add discount if present
    if (descontoAmount > 0) {
      doc.setTextColor(255, 0, 0); // Red color
      doc.text('Desconto', margin, yPosition);
      doc.text(`-${formatBRL(descontoAmount)}`, pageWidth - margin - 30, yPosition, { align: 'right' });
      yPosition += 5;
      doc.setTextColor(0, 0, 0); // Reset to black
    }

    // Total Pedido em destaque
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Total Pedido', margin, yPosition);
    doc.text(formatBRL(totalPedido), pageWidth - margin - 30, yPosition, { align: 'right' });
    yPosition += 5;
  };

  // Função para adicionar rodapé em todas as páginas
  const addFooter = (pageNumber) => {
    const footerY = pageHeight - 25;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`ORÇAMENTO`, pageWidth / 2, footerY, { align: 'center' });
    doc.text(`${quotationId}.${version}`, pageWidth / 2, footerY + 5, { align: 'center' });
    doc.text(`V`, pageWidth / 2, footerY + 10, { align: 'center' });
    doc.text(`P.${pageNumber}`, pageWidth / 2, footerY + 15, { align: 'center' });

    doc.line(margin, footerY - 5, margin + 60, footerY - 5);
    doc.line(pageWidth - margin - 60, footerY - 5, pageWidth - margin, footerY - 5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Autorizada a compra', margin + 10, footerY);
    doc.text('Assinatura/Carimbo', pageWidth - margin - 50, footerY);
  };

  // ===== PÁGINA 1 =====
  addWatermark();
  addHeader();
  addClientData();
  addMaterialsTable();

  // Verificar se precisa de nova página antes de valores do pedido
  // Espaço necessário: título (7) + 2-3 linhas de valores (15) + margem = ~70
  if (yPosition > pageHeight - 70) {
    addFooter(1);
    doc.addPage();
    yPosition = margin;
    addWatermark();
    addHeader();
  }

  addOrderValues();
  addFooter(1);

  // Verificar se há espaço suficiente para observações na mesma página
  if (yPosition > pageHeight - 80) {
    doc.addPage();
    yPosition = margin;
    addWatermark();
    addHeader();
    addFooter(doc.internal.getNumberOfPages());
  }

  // Observações
  if (formData.observacao) {
    // Verificar se há espaço suficiente para pelo menos o título + 1 linha de conteúdo
    if (yPosition > pageHeight - 80) {
      doc.addPage();
      yPosition = margin;
      addWatermark();
      addHeader();
      addFooter(doc.internal.getNumberOfPages());
    }

    doc.setFillColor(...headerBg);
    doc.rect(margin, yPosition, pageWidth - 2 * margin, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('OBSERVAÇÕES', margin + 2, yPosition + 5);
    yPosition += 12;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const observacaoLines = doc.splitTextToSize(formData.observacao, pageWidth - 2 * margin);
    for (const line of observacaoLines) {
        if (yPosition > pageHeight - 50) {
          doc.addPage();
          yPosition = margin;
          addWatermark();
          addHeader();
          addFooter(doc.internal.getNumberOfPages());
        }
      doc.text(line, margin, yPosition);
      yPosition += 5;
    }
    yPosition += 10;
  }

  // Informações comerciais
  // Verificar se há espaço suficiente para pelo menos o título + 1 linha de conteúdo
  if (yPosition > pageHeight - 80) {
    doc.addPage();
    yPosition = margin;
    addWatermark();
    addHeader();
    addFooter(doc.internal.getNumberOfPages());
  }

  doc.setFillColor(...headerBg);
  doc.rect(margin, yPosition, pageWidth - 2 * margin, 8, 'F');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('INFORMAÇÕES COMERCIAIS', margin + 2, yPosition + 5);
  yPosition += 12;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  const comercialData = [
    ['FRETE:', formData.tipoFrete || ''],
    ['Contra Aviso:', formData.contraAviso || ''],
    ['Ordem de Compra:', formData.ordemCompra || ''],
    ['Condição de pagamento:', formData.condicaoPagamento || ''],
    ['Forma de pagamento:', formData.formaPagamento || ''],
  ];

  comercialData.forEach(([label, value]) => {
    // Verificar espaço antes de cada linha
    if (yPosition > pageHeight - 50) {
      doc.addPage();
      yPosition = margin;
      addWatermark();
      addHeader();
      addFooter(doc.internal.getNumberOfPages());
    }
    doc.setFont('helvetica', 'bold');
    doc.text(label, margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(value, margin + 50, yPosition);
    yPosition += 5;
  });
  yPosition += 5;

  // Verificar espaço suficiente para endereços (estimativa: ~30 linhas)
  if (yPosition > pageHeight - 130) {
    doc.addPage();
    yPosition = margin;
    addWatermark();
    addHeader();
    addFooter(doc.internal.getNumberOfPages());
  }

  // Endereços
  const col1X = margin;
  const col2X = pageWidth / 2 + 5;
  const maxWidthPerColumn = (pageWidth / 2) - margin - 5;

  // Endereços lado a lado
  const startY = yPosition;
  
  // Endereço de entrega (coluna esquerda)
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Endereço de entrega', col1X, yPosition);
  yPosition += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  const isDifferent = formData.cidadeDiferente === 'Sim';
  
  // Endereço - com quebra de linha
  const enderecoEntregaTexto = `${isDifferent ? formData.logradouroEntregaDiferente : formData.logradouro || ''}, ${isDifferent ? formData.numeroEntregaDiferente : formData.numero || ''}`;
  doc.text('Endereço: ', col1X, yPosition);
  const enderecoEntregaLines = doc.splitTextToSize(enderecoEntregaTexto, maxWidthPerColumn - 20);
  enderecoEntregaLines.forEach((line, idx) => {
    doc.text(line, col1X + 20, yPosition + (idx * 4));
  });
  yPosition += enderecoEntregaLines.length * 4;

  // Complemento - com quebra de linha
  const complementoEntregaTexto = isDifferent ? formData.complementoEntregaDiferente : formData.complemento || '';
  if (complementoEntregaTexto) {
    doc.text('Complemento: ', col1X, yPosition);
    const complementoEntregaLines = doc.splitTextToSize(complementoEntregaTexto, maxWidthPerColumn - 25);
    complementoEntregaLines.forEach((line, idx) => {
      doc.text(line, col1X + 25, yPosition + (idx * 4));
    });
    yPosition += complementoEntregaLines.length * 4;
  } else {
    doc.text('Complemento: ', col1X, yPosition);
    yPosition += 4;
  }

  // Demais campos sem quebra
  doc.text(`Cidade: ${truncateText(isDifferent ? formData.cidadeEntregaDiferente : formData.cidade || '', 54)}`, col1X, yPosition);
  yPosition += 4;
  doc.text(`Bairro: ${truncateText(isDifferent ? formData.bairroEntregaDiferente : formData.bairro || '', 54)}`, col1X, yPosition);
  yPosition += 4;
  doc.text(`CEP: ${isDifferent ? formData.cepEntregaDiferente : formData.cep || ''}`, col1X, yPosition);
  yPosition += 4;
  doc.text(`Estado: ${truncateText(isDifferent ? formData.estadoEntregaDiferente : formData.estado || '', 54)}`, col1X, yPosition);

  // Resetar yPosition para endereço de cobrança (coluna direita)
  yPosition = startY;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Endereço de cobrança', col2X, yPosition);
  yPosition += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  // Endereço - com quebra de linha
  const enderecoCobrancaTexto = `${formData.logradouro || ''}, ${formData.numero || ''}`;
  doc.text('Endereço: ', col2X, yPosition);
  const enderecoCobrancaLines = doc.splitTextToSize(enderecoCobrancaTexto, maxWidthPerColumn - 20);
  enderecoCobrancaLines.forEach((line, idx) => {
    doc.text(line, col2X + 20, yPosition + (idx * 4));
  });
  yPosition += enderecoCobrancaLines.length * 4;

  // Complemento - com quebra de linha
  const complementoCobrancaTexto = formData.complemento || '';
  if (complementoCobrancaTexto) {
    doc.text('Complemento: ', col2X, yPosition);
    const complementoCobrancaLines = doc.splitTextToSize(complementoCobrancaTexto, maxWidthPerColumn - 25);
    complementoCobrancaLines.forEach((line, idx) => {
      doc.text(line, col2X + 25, yPosition + (idx * 4));
    });
    yPosition += complementoCobrancaLines.length * 4;
  } else {
    doc.text('Complemento: ', col2X, yPosition);
    yPosition += 4;
  }

  // Demais campos sem quebra
  doc.text(`Cidade: ${truncateText(formData.cidade || '', 54)}`, col2X, yPosition);
  yPosition += 4;
  doc.text(`Bairro: ${truncateText(formData.bairro || '', 54)}`, col2X, yPosition);
  yPosition += 4;
  doc.text(`CEP: ${formData.cep || ''}`, col2X, yPosition);
  yPosition += 4;
  doc.text(`Estado: ${truncateText(formData.estado || '', 54)}`, col2X, yPosition);
  yPosition += 4;

  yPosition += 6;

  // Cláusulas do contrato
  const addClausulas = async () => {
    if (clausulas && clausulas.length > 0) {
      // Verificar se há espaço suficiente para pelo menos o título + 1 linha de conteúdo
      if (yPosition > pageHeight - 80) {
        doc.addPage();
        yPosition = margin;
        addWatermark();
        addHeader();
        addFooter(doc.internal.getNumberOfPages());
      }

      doc.setFillColor(...headerBg);
      doc.rect(margin, yPosition, pageWidth - 2 * margin, 8, 'F');
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('CLÁUSULAS DO CONTRATO', margin + 2, yPosition + 5);
      yPosition += 12;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');

      for (let index = 0; index < clausulas.length; index++) {
        const clausula = clausulas[index];

        if (yPosition > pageHeight - 50) {
          doc.addPage();
          yPosition = margin;
          addWatermark();
          addHeader();
          addFooter(doc.internal.getNumberOfPages());
        }

        const clausulaText = `${index + 1}. ${clausula.texto}`;
        const lines = doc.splitTextToSize(clausulaText, pageWidth - 2 * margin);

        for (const line of lines) {
          if (yPosition > pageHeight - 50) {
            doc.addPage();
            yPosition = margin;
            addWatermark();
            addHeader();
            addFooter(doc.internal.getNumberOfPages());
          }
          doc.text(line, margin, yPosition);
          yPosition += 4;
        }
        yPosition += 2;
      }
    }
  };

  await addClausulas();

  yPosition += 5;

  // Informações para Depósito
  // Calcular espaço necessário: header (12) + rect (30) + bank data (~25) + margem + espaço para footer = ~90
  const requiredSpace = 90;
  if (yPosition > pageHeight - requiredSpace) {
    doc.addPage();
    yPosition = margin;
    addWatermark();
    addHeader();
  }

  doc.setFillColor(...headerBg);
  doc.rect(margin, yPosition, pageWidth - 2 * margin, 8, 'F');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('INFORMAÇÕES PARA DEPÓSITO', margin + 2, yPosition + 5);
  yPosition += 12;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  // Dados bancários em caixa
  doc.setDrawColor(200, 200, 200);
  doc.rect(margin, yPosition, pageWidth - 2 * margin, 30);
  yPosition += 5;

  const bankData = [
    `Banco ${filialData?.banco || ''}: (AG.:${filialData?.agencia || ''}) (C/C.:${filialData?.conta_corrente || ''})`,
    `Favorecido: ${filialData?.nome || ''}`,
    `CNPJ: ${filialData?.cnpj || ''}`,
    `Chave Pix: ${filialData?.pix || ''}`,
    `Contato: ${filialData?.telefone || ''}`
  ];

  bankData.forEach(line => {
    doc.text(line, margin + 2, yPosition);
    yPosition += 5;
  });

  yPosition += 5;

  // Verificar se há espaço suficiente para o rodapé, senão adicionar nova página
  if (yPosition > pageHeight - 50) {
    doc.addPage();
    yPosition = margin;
    addWatermark();
    addHeader();
  }

  // Rodapé final
  const footerY = pageHeight - 25;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(`ORÇAMENTO`, pageWidth / 2, footerY, { align: 'center' });
  doc.text(`${quotationId}.${version}`, pageWidth / 2, footerY + 5, { align: 'center' });
  doc.text(`V`, pageWidth / 2, footerY + 10, { align: 'center' });
  doc.text(`P.${doc.internal.getNumberOfPages()}`, pageWidth / 2, footerY + 15, { align: 'center' });

  doc.line(margin, footerY - 5, margin + 60, footerY - 5);
  doc.line(pageWidth - margin - 60, footerY - 5, pageWidth - margin, footerY - 5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Autorizada a compra', margin + 10, footerY);
  doc.text('Assinatura/Carimbo', pageWidth - margin - 50, footerY);

  // Salvar PDF
  doc.save(`Orcamento_${quotationId}_v${version}.pdf`);
};
